import React from 'react';
import { Alert, Box, Button, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { CalendarMonth as CalendarIcon, EditCalendar as ManualIcon, Groups as TeamsIcon, Mic, VideoCall as ZoomIcon } from '@mui/icons-material';

const toggleSx = {
  '& .MuiToggleButton-root': {
    border: '1.5px solid #E5E7EB',
    borderRadius: '12px',
    px: 2,
    py: 0.75,
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#6B7280',
    textTransform: 'none',
    gap: 0.75,
    '&.Mui-selected': { backgroundColor: '#E3F2FD', color: '#1565C0', borderColor: '#90CAF9', fontWeight: 600, '&:hover': { backgroundColor: '#BBDEFB' } },
    '&:hover': { backgroundColor: '#F9FAFB', borderColor: '#D1D5DB' },
    '&.Mui-disabled': { opacity: 0.4 },
  },
  gap: 1,
};

interface SchedulingMethodSelectorProps {
  mode: 'create' | 'edit' | 'upload';
  schedulingMethod: 'default' | 'calendly';
  meetingMedium: string;
  isZoomEnabled: boolean;
  isMicrosoftConnected: boolean;
  onSchedulingMethodChange: (method: 'default' | 'calendly') => void;
  onMeetingMediumChange: (medium: string) => void;
  onConnectMicrosoft: () => void;
}

const SchedulingMethodSelector: React.FC<SchedulingMethodSelectorProps> = ({
  mode,
  schedulingMethod,
  meetingMedium,
  isZoomEnabled,
  isMicrosoftConnected,
  onSchedulingMethodChange,
  onMeetingMediumChange,
  onConnectMicrosoft,
}) => (
  <>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
      {mode === 'create' && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500, fontSize: '0.8rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            Scheduling Method
          </Typography>
          <ToggleButtonGroup value={schedulingMethod} exclusive size="small" sx={toggleSx}
            onChange={(_, val) => { if (val) onSchedulingMethodChange(val); }}
          >
            <ToggleButton value="default" disableRipple><ManualIcon sx={{ fontSize: 18 }} /> Manual</ToggleButton>
            <ToggleButton value="calendly" disableRipple><CalendarIcon sx={{ fontSize: 18 }} /> Calendly</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
      {schedulingMethod !== 'calendly' && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500, fontSize: '0.8rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            Meeting Medium
          </Typography>
          <ToggleButtonGroup value={meetingMedium} exclusive size="small" sx={toggleSx}
            onChange={(_, val) => { if (val) onMeetingMediumChange(val); }}
          >
            <ToggleButton value="PC / Microphone" disableRipple><Mic sx={{ fontSize: 18 }} /> In Person</ToggleButton>
            <ToggleButton value="Teams Meeting" disableRipple><TeamsIcon sx={{ fontSize: 18 }} /> Teams</ToggleButton>
            <ToggleButton value="Zoom" disabled={!isZoomEnabled} disableRipple><ZoomIcon sx={{ fontSize: 18 }} /> Zoom</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
    </Stack>
    {schedulingMethod !== 'calendly' && meetingMedium === 'Teams Meeting' && !isMicrosoftConnected && (
      <Alert severity="info"
        action={<Button color="primary" size="small" variant="contained" onClick={onConnectMicrosoft}>Connect Now</Button>}
        sx={{ bgcolor: 'primary.light', color: 'primary.dark', '& .MuiAlert-icon': { color: 'primary.main' } }}
      >
        Connect your Microsoft account to import recordings after the meeting.
      </Alert>
    )}
  </>
);

export default SchedulingMethodSelector;
